import React from "react";
import styled from "@emotion/styled";
import { ProjectCharge } from "~/gql/types";
import { useEeCodes } from "~/refdata2/eeCodes";
import Spinner from "~/components/spinner";
import { getInitialFormValues } from "~/project-charge-form/functions/getInitialFormValues";
import { resolveInitialEeCode } from "~/project-charge-form/functions/resolveInitialEeCode";
import { ChargeFormGraphqlProvider } from "~/project-charge-form/chargeFormProviders/ChargeFormGraphqlApi";
import { ChargeForm } from "./ChargeForm";

type ChargeEditorProps = {
  charge: ProjectCharge;
  onSaved: () => void;
  onCancel: () => void;
};

const FormBody = styled.div`
  margin: 24px;
`;

/**
 * Presentational editor page: the host resolves the charge and hands it here.
 * Composes the GraphQL provider + the (unchanged) form. Mirrors what the old
 * `EditProjectChargePage` did, minus the context stash and the "No charge found"
 * fallback (a missing or malformed charge never reaches here — the outlet guards
 * both edges, so `charge.project` is present).
 *
 * The elevated page chrome is the app's own breadcrumb bar (rendered in the
 * elevated top app bar); the outlet owns the breadcrumb trail (so it is present
 * during the loading spinner too), and no separate header component is needed.
 * Dismissal is the form body's own Cancel (wired through `onCancel` →
 * `host.dismiss()`, filters preserved) and the breadcrumb links.
 *
 * Shows a spinner until the EE codes load, then seeds the form with the
 * charge's EE code record (`resolveInitialEeCode` throws if there is none).
 */
export const ChargeEditor: React.FC<ChargeEditorProps> = ({
  charge,
  onSaved,
  onCancel,
}) => {
  const eeCodes = useEeCodes();

  if (!eeCodes) {
    return <Spinner open />;
  }

  const eeCode = resolveInitialEeCode(charge, eeCodes);
  const initialValues = getInitialFormValues(charge, eeCode, charge.project!);

  return (
    <ChargeFormGraphqlProvider>
      <FormBody>
        <ChargeForm
          charge={charge}
          initialValues={initialValues}
          onSaved={onSaved}
          onCancel={onCancel}
        />
      </FormBody>
    </ChargeFormGraphqlProvider>
  );
};
